import React from 'react';
import { useTranslation } from 'react-i18next';

import { WIZARD_STEPS, type WizardStep } from '../../../store/configurator';
import { CFG_CHROME } from '../../../../../theme/configurator-tokens';
import { Header, Names, Name, TrackWrap, Track, Indicator, Labels, LabelCell, StepLabel } from './ProgressHeader.styles';

const TRACK_WIDTH = 355;

const CheckCircle = (): React.ReactElement => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <circle cx="6" cy="6" r="6" fill={CFG_CHROME.check} />
    <path
      d="M3.5 6.2 5.1 7.8 8.5 4.4"
      stroke={CFG_CHROME.checkStroke}
      strokeWidth="1.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type ProgressHeaderProps = {
  /** Zero-based index of the current step. */
  currentStep: number;
};

/** Step names + progress bar + per-step "Step N" / check labels. */
export const ProgressHeader = ({ currentStep }: ProgressHeaderProps): React.ReactElement => {
  const { t } = useTranslation();

  // Step names are a fixed, closed vocabulary (Theme / Colors & Art Style / Payment review /
  // Payment). WIZARD_STEPS stays English at module scope (it also keys navigation/paths);
  // translate at this render site, keyed on the stable `step.key`, so the labels stay reactive
  // on a language change.
  const stepName = (step: WizardStep): string => {
    switch (step.key) {
      case 'theme':
        return t('Theme');
      case 'colors':
        return t('Style');
      case 'review':
        return t('Review');
      case 'payment':
        return t('Payment');
      default:
        return step.name;
    }
  };

  // The indicator reaches the centre of the current step's column.
  const columnWidth = TRACK_WIDTH / WIZARD_STEPS.length;
  const indicatorWidth = (currentStep + 0.5) * columnWidth;

  return (
    <Header>
      <Names>
        {WIZARD_STEPS.map((step, i) => (
          <Name key={step.key} text={stepName(step)} $active={i === currentStep} />
        ))}
      </Names>

      <TrackWrap>
        <Track />
        <Indicator $pct={(indicatorWidth / TRACK_WIDTH) * 100} />
      </TrackWrap>

      <Labels>
        {WIZARD_STEPS.map((step, i) => (
          <LabelCell key={step.key}>
            {i < currentStep ? (
              <CheckCircle />
            ) : (
              <StepLabel text="Step {{n}}" placeholders={{ n: i + 1 }} $active={i === currentStep} />
            )}
          </LabelCell>
        ))}
      </Labels>
    </Header>
  );
};

export default ProgressHeader;
